import type { ReactNode } from 'react';
import { Icon } from '../components/icons';
import { Button } from '../components/ui';
import { euro } from '../format';
import { isReduced } from '../pos/promo';
import { saleNotices } from '../pos/saleNotices';
import { sendState, type SendContext } from '../pos/sendState';
import type { LocalSale, SaleLineView } from '../pos/types';
import { voidReasonLine } from '../pos/voidText';

/**
 * Karte eines Verkaufs im Blatt „Verkäufe dieser Kasse“ und im Blatt „Letzter Verkauf“ (U17e). Zugeklappt steht eine Zeile
 * mit Nummer, Uhrzeit, Betrag und Status-Chip. Aufgeklappt kommen Positionen, Hinweise in Klartext, der Sendestatus mit
 * Erklärung und die Aktionen dazu. Die Karte zeigt nur an und meldet Wünsche zurück; Stornieren fragt die Seite immer über
 * das Rückfrage-Blatt, nie über eine Geste.
 */

const TIME = new Intl.DateTimeFormat('de-DE', {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Europe/Berlin',
});

const percentText = (basisPoints: number): string => `${String(basisPoints / 100)} %`;

/** Zusatz einer Position: Aktion (X7) oder Preis an der Kasse korrigiert (U17g, eine Zeile hat nie beides). */
function lineExtra(line: SaleLineView): string {
  if (line.priceChange !== undefined)
    return ` (korrigiert, statt ${euro(line.priceChange.originalCents)})`;
  if (isReduced(line) && line.promo !== null && line.promo !== undefined) {
    return ` (Aktion −${percentText(line.promo.basisPoints)}, statt ${euro(line.promo.listPriceCents)})`;
  }
  return '';
}

/** Sendestatus samt „Jetzt senden“: der Zustand der Verbindung kommt von der Seite. */
export interface SaleSend extends SendContext {
  onSendNow: () => void;
}

/** Kurztext des Sendestatus für den Namen eines Symbols („Gesendet 14:31“, ohne Häkchen). */
export function sendLabel(sale: LocalSale, context: SendContext): string {
  return sendState(sale, context).chip.replace('✓', '').replace(/\s+/g, ' ').trim();
}

export function SaleCard({
  sale,
  send,
  expanded,
  onToggle,
  cartEmpty,
  onVoid,
  onCorrect,
  onNote,
  extra,
}: {
  sale: LocalSale;
  send: SaleSend;
  expanded: boolean;
  /** Ohne Angabe ist die Karte immer aufgeklappt und hat keinen Umschalter (Blatt „Letzter Verkauf“). */
  onToggle?: (() => void) | undefined;
  /** „Korrigieren“ geht nur bei leerem Warenkorb. */
  cartEmpty: boolean;
  onVoid: (sale: LocalSale, lineNo?: number) => void;
  onCorrect: (sale: LocalSale) => void;
  /** „Problem melden“ zu diesem Verkauf (U17f). */
  onNote?: ((sale: LocalSale) => void) | undefined;
  /** Zusatz im aufgeklappten Teil, z. B. der Stand gemeldeter Probleme (U17f). */
  extra?: ReactNode;
}) {
  const state = sendState(sale, send);
  const notices = saleNotices(sale);
  // Grund des Stornos (U17d): bei einem ganz stornierten Verkauf auch zugeklappt sichtbar, bei Teilstornos aufgeklappt
  const reasonLine = voidReasonLine(sale);
  const title = `Nr. ${String(sale.registerSeq)} · ${TIME.format(sale.soldAtMs)} · ${euro(sale.totalCents)}`;
  // Storniert und alles bestätigt (oder der Verkauf war ohnehin nicht übernommen): grau. Wartet noch etwas (Verkauf oder
  // Storno), zeigt der Chip das, nie „storniert“ allein
  const settled = sale.voided && (state.kind === 'sent' || sale.state === 'failed');
  const chipText = settled
    ? 'storniert'
    : sale.voided && sale.state === 'pending'
      ? `storniert, ${state.chip}`
      : state.chip;
  const chip = (
    <span className={`badge sale-card__chip sale-card__chip--${settled ? 'muted' : state.tone}`}>
      {chipText}
    </span>
  );
  const head = (
    <>
      <strong>{title}</strong>
      {chip}
    </>
  );
  return (
    <li className={`sale-card${sale.voided ? ' sale-card--voided' : ''}`}>
      {onToggle === undefined ? (
        <div className="sale-card__head">{head}</div>
      ) : (
        <button
          type="button"
          className="sale-card__head"
          aria-expanded={expanded}
          aria-label={`${title}, ${chipText.replace('✓', '').replace(/\s+/g, ' ').trim()}`}
          onClick={onToggle}
        >
          {head}
          <span className={`sale-card__arrow${expanded ? ' sale-card__arrow--open' : ''}`}>
            <Icon name="chevron-down" size={20} />
          </span>
        </button>
      )}
      {reasonLine !== null && (sale.voided || expanded) && (
        <p className="muted sale-card__reason">{reasonLine}</p>
      )}
      {expanded && (
        <div className="sale-card__body stack">
          {sale.payment === 'card' && (
            <p>{`Zahlart: Karte${sale.sentAsCash === true ? ' (als bar übertragen)' : ''}`}</p>
          )}
          {sale.givenCents !== null && (
            <p>{`Gegeben ${euro(sale.givenCents)} · Rückgeld ${euro(sale.givenCents - sale.totalCents)}`}</p>
          )}
          <ul className="sale-card__lines">
            {sale.lines.map((line, index) => {
              const text = `${String(index + 1)}. ${line.title} · ${euro(line.priceCents)}${lineExtra(line)}`;
              const open = !line.voided && !sale.voided;
              return (
                <li key={line.code}>
                  {open ? (
                    <button
                      type="button"
                      className="sale-card__line"
                      aria-label={`Position ${String(index + 1)} stornieren: ${line.title}`}
                      onClick={() => {
                        onVoid(sale, index + 1);
                      }}
                    >
                      <span>{text}</span>
                      <span className="sale-card__line-act" aria-hidden="true">
                        <Icon name="close" size={18} />
                      </span>
                    </button>
                  ) : (
                    <span className="sale-card__line sale-card__line--voided">{text}</span>
                  )}
                </li>
              );
            })}
          </ul>
          {notices.map((notice) => (
            <p key={notice.id} className={`sale-notice sale-notice--${notice.tone}`}>
              <Icon name="alert" size={18} />
              <span>
                {notice.text}
                {notice.detail !== undefined && notice.detail !== '' && (
                  <span className="muted sale-notice__detail">{` (${notice.detail})`}</span>
                )}
              </span>
            </p>
          ))}
          {/* Bei „Nicht übernommen“ (Verkauf) steht der Text schon als roter Hinweis oben; ein offenes Storno wird erklärt */}
          {!settled && sale.state !== 'failed' && (
            <p className="muted sale-card__explain">
              {state.explain}
              {state.canSendNow && (
                <>
                  {' '}
                  <Button variant="secondary" small onClick={send.onSendNow}>
                    Jetzt senden
                  </Button>
                </>
              )}
            </p>
          )}
          {extra}
          {(!sale.voided || onNote !== undefined) && (
            <div className="row sale-card__actions">
              {!sale.voided && (
                <>
                  <Button
                    variant="secondary"
                    disabled={!cartEmpty}
                    onClick={() => {
                      onCorrect(sale);
                    }}
                  >
                    Korrigieren
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => {
                      onVoid(sale);
                    }}
                  >
                    Verkauf stornieren
                  </Button>
                </>
              )}
              {onNote !== undefined && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    onNote(sale);
                  }}
                >
                  Problem melden
                </Button>
              )}
            </div>
          )}
          {!sale.voided && !cartEmpty && (
            <p className="muted" role="status">
              Bitte den aktuellen Warenkorb erst parken oder kassieren.
            </p>
          )}
        </div>
      )}
    </li>
  );
}
